import { Linking } from 'react-native';
import { Platform } from 'utils/platform';
export type MailtoFields = {
  to: string;
  subject: string;
  body: string;
  cc?: string;
  bcc?: string;
};
const GMAIL_WEB_SAFE_MAX_URL_LENGTH = 7800;

const TRUNCATION_NOTICE =
  '\n\n[Texto truncado automaticamente — limite seguro de URL no navegador.]';

function decodeMailtoPathSegment(raw: string): string {
  if (!raw) return '';
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw;
  }
}
export function parseMailtoHref(href: string): MailtoFields {
  if (!/^mailto:/i.test(href)) {
    return { to: '', subject: '', body: '' };
  }

  const rest = href.slice('mailto:'.length);
  const qIndex = rest.indexOf('?');
  const rawTo = qIndex === -1 ? rest : rest.slice(0, qIndex);
  const params =
    qIndex === -1 ? new URLSearchParams() : new URLSearchParams(rest.slice(qIndex + 1));

  return {
    to: decodeMailtoPathSegment(rawTo),
    subject: params.get('subject') ?? '',
    body: params.get('body') ?? '',
    cc: params.get('cc') ?? undefined,
    bcc: params.get('bcc') ?? undefined,
  };
}
export function mailtoHrefFromFields(fields: MailtoFields): string {
  const params = new URLSearchParams();
  if (fields.subject) params.set('subject', fields.subject);
  if (fields.body) params.set('body', fields.body);
  if (fields.cc) params.set('cc', fields.cc);
  if (fields.bcc) params.set('bcc', fields.bcc);

  const query = params.toString();
  const path = fields.to;
  return query ? `mailto:${path}?${query}` : `mailto:${path}`;
}
export function buildGmailWebComposeUrl(fields: MailtoFields): string {
  const u = new URL('https://mail.google.com/mail/');
  u.searchParams.set('view', 'cm');
  u.searchParams.set('fs', '1');
  if (fields.to) u.searchParams.set('to', fields.to);
  if (fields.subject) u.searchParams.set('su', fields.subject);
  if (fields.body) u.searchParams.set('body', fields.body);
  if (fields.cc) u.searchParams.set('cc', fields.cc);
  if (fields.bcc) u.searchParams.set('bcc', fields.bcc);

  return u.toString();
}

function truncateFieldsForUrlLimit(fields: MailtoFields): MailtoFields {
  if (!fields.body) return fields;

  let next: MailtoFields = { ...fields };
  let url = buildGmailWebComposeUrl(next);
  let iterations = 0;
  const maxIterations = 48;

  while (
    url.length > GMAIL_WEB_SAFE_MAX_URL_LENGTH &&
    next.body.length > TRUNCATION_NOTICE.length &&
    iterations < maxIterations
  ) {
    iterations += 1;
    next = {
      ...next,
      body: next.body.slice(0, Math.floor(next.body.length * 0.85)) + TRUNCATION_NOTICE,
    };
    url = buildGmailWebComposeUrl(next);
  }

  return next;
}
export function openMailComposerFromHref(href: string): Promise<void> {
  if (Platform.OS !== 'web' || !/^mailto:/i.test(href)) {
    return Linking.openURL(href);
  }

  if (typeof window === 'undefined') {
    return Linking.openURL(href);
  }
  if (Platform.isMobileWeb()) {
    return Linking.openURL(href);
  }

  const fields = truncateFieldsForUrlLimit(parseMailtoHref(href));
  const gmailUrl = buildGmailWebComposeUrl(fields);

  try {
    const opened = window.open(gmailUrl, '_blank');
    if (opened) {
      opened.opener = null;
    }
    if (!opened) {
      return Linking.openURL(mailtoHrefFromFields(fields));
    }
    return Promise.resolve();
  } catch {
    return Linking.openURL(mailtoHrefFromFields(fields));
  }
}
